import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// LLM Provider configurations with models
const LLM_PROVIDERS = {
  openai: {
    name: 'OpenAI',
    description: 'GPT-4 and GPT-3.5 models',
    icon: '🤖',
    keyPlaceholder: 'sk-...',
    helpUrl: 'https://platform.openai.com/api-keys',
    models: [
      { id: 'gpt-4o', name: 'GPT-4o', version: 'Latest', description: 'Most capable model for complex tasks' },
      { id: 'gpt-4o-mini', name: 'GPT-4o Mini', version: 'Latest', description: 'Fast and cost-effective' },
      { id: 'gpt-4', name: 'GPT-4', version: 'Stable', description: 'High intelligence model' },
      { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', version: '16K', description: 'Fast and affordable' },
      { id: 'gpt-3.5-turbo-16k', name: 'GPT-3.5 Turbo 16K', version: 'Extended', description: 'Extended context window' },
    ]
  },
  local_llm: {
    name: 'Local LLM',
    description: 'Self-hosted LLM server',
    icon: '🏠',
    keyPlaceholder: 'Not required for local LLM',
    helpUrl: '#',
    models: [
      { id: 'qwen2.5:7b', name: 'Qwen 2.5 7B', version: 'Latest', description: 'Efficient local model' },
      { id: 'qwen2.5:14b', name: 'Qwen 2.5 14B', version: 'Latest', description: 'Larger model for complex tasks' },
      { id: 'llama3.2:latest', name: 'Llama 3.2', version: 'Latest', description: 'Meta\'s open model' },
      { id: 'mistral:latest', name: 'Mistral', version: 'Latest', description: 'Fast and efficient' },
    ]
  },
  anthropic: {
    name: 'Anthropic',
    description: 'Claude 3 models',
    icon: '🧠',
    keyPlaceholder: 'sk-ant-...',
    helpUrl: 'https://console.anthropic.com/',
    models: [
      { id: 'claude-3-5-sonnet-latest', name: 'Claude 3.5 Sonnet', version: 'Latest', description: 'Best balance of speed and intelligence' },
      { id: 'claude-3-5-haiku-latest', name: 'Claude 3.5 Haiku', version: 'Latest', description: 'Fastest model for quick tasks' },
      { id: 'claude-3-opus-latest', name: 'Claude 3 Opus', version: 'Latest', description: 'Most powerful for complex tasks' },
      { id: 'claude-3-sonnet-20240229', name: 'Claude 3 Sonnet', version: '2024-02', description: 'Balanced performance' },
      { id: 'claude-3-haiku-20240307', name: 'Claude 3 Haiku', version: '2024-03', description: 'Fast and efficient' },
    ]
  },
  google: {
    name: 'Google AI',
    description: 'Gemini models',
    icon: '✨',
    keyPlaceholder: 'AIza...',
    helpUrl: 'https://makersuite.google.com/app/apikey',
    models: [
      { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro', version: 'Latest', description: 'Most capable Gemini model' },
      { id: 'gemini-1.5-flash', name: 'Gemini 1.5 Flash', version: 'Latest', description: 'Fast and versatile' },
      { id: 'gemini-1.0-pro', name: 'Gemini 1.0 Pro', version: 'Stable', description: 'Reliable general model' },
      { id: 'gemini-pro', name: 'Gemini Pro', version: 'Legacy', description: 'Text generation model' },
    ]
  }
};

// Searchable Dropdown Component
const ModelDropdown = ({ models, selectedModel, onSelect, isLoading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredModels = models.filter(model => 
    model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    model.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    model.version.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedModelData = models.find(m => m.id === selectedModel);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) setTimeout(() => inputRef.current?.focus(), 100);
        }}
        disabled={isLoading}
        className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-white text-left flex items-center justify-between hover:border-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <div className="flex items-center space-x-2 text-slate-500">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
            <span>Loading models...</span>
          </div>
        ) : selectedModelData ? (
          <div className="flex items-center justify-between w-full">
            <div>
              <span className="font-medium text-slate-900">{selectedModelData.name}</span>
              <span className="ml-2 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                {selectedModelData.version}
              </span>
            </div>
          </div>
        ) : (
          <span className="text-slate-400">Select a model...</span>
        )}
        <svg className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                placeholder="Search models..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Model List */}
          <div className="max-h-64 overflow-y-auto">
            {filteredModels.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-500 text-center">
                No models found
              </div>
            ) : (
              filteredModels.map((model) => (
                <button
                  key={model.id}
                  type="button"
                  onClick={() => {
                    onSelect(model.id);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors flex items-center justify-between ${
                    selectedModel === model.id ? 'bg-indigo-50' : ''
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className={`font-medium ${selectedModel === model.id ? 'text-indigo-700' : 'text-slate-900'}`}>
                        {model.name}
                      </span>
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                        {model.version}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{model.description}</p>
                  </div>
                  {selectedModel === model.id && (
                    <svg className="w-5 h-5 text-indigo-600 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const LlmSettings = () => {
  // Form state
  const [formData, setFormData] = useState({
    provider: 'openai',
    api_key: '',
    model: '',
    temperature: 0.7,
    max_tokens: 2000,
    local_llm_url: '',
    local_llm_stream: false
  });

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  
  // Status state
  const [currentConfig, setCurrentConfig] = useState(null);
  const [testResponse, setTestResponse] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Store all provider configs from database
  const [allProviderConfigs, setAllProviderConfigs] = useState({});

  // Load all provider configurations from database
  const loadAllProviderConfigs = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/llm-config/all`);
      if (response.data && response.data.configs) {
        const configsMap = {};
        response.data.configs.forEach(config => {
          configsMap[config.provider] = config;
        });
        setAllProviderConfigs(configsMap);
      }
    } catch (error) {
      console.error('Error loading all provider configs:', error);
      // If endpoint doesn't exist, continue with empty configs
    }
  };

  // Load existing configuration
  useEffect(() => {
    const initializeConfigs = async () => {
      await loadAllProviderConfigs();
      await loadConfiguration();
    };
    initializeConfigs();
  }, []);

  const loadConfiguration = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/llm-config`);
      if (response.data) {
        const config = response.data;
        setCurrentConfig(config);
        setFormData({
          provider: config.provider,
          api_key: config.api_key || '', // Load API key from database
          model: config.model,
          temperature: config.temperature,
          max_tokens: config.max_tokens,
          local_llm_url: config.local_llm_url || 'https://privatellm.kadellabs.com/api/chat',
          local_llm_stream: config.local_llm_stream || false
        });
      }
    } catch (error) {
      console.error('Error loading LLM config:', error);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
  };

  const handleProviderChange = (provider) => {
    const providerConfig = LLM_PROVIDERS[provider];
    const dbConfig = allProviderConfigs[provider];
    
    // Use database values if available, otherwise use empty strings (no hardcoded defaults)
    setFormData({
      ...formData,
      provider,
      model: dbConfig?.model || providerConfig.models[0]?.id || '',
      api_key: dbConfig?.api_key || '',
      local_llm_url: dbConfig?.local_llm_url || '',
      local_llm_stream: dbConfig?.local_llm_stream !== undefined ? dbConfig.local_llm_stream : false,
      temperature: dbConfig?.temperature || 0.7,
      max_tokens: dbConfig?.max_tokens || 2000
    });
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const validateForm = () => {
    // For local_llm, API key is not required
    if (formData.provider !== 'local_llm' && !formData.api_key && !currentConfig) {
      showToast('Please enter your API key', 'error');
      return false;
    }
    if (formData.provider === 'local_llm' && !formData.local_llm_url) {
      showToast('Please enter the Local LLM URL', 'error');
      return false;
    }
    if (!formData.model) {
      showToast('Please select a model', 'error');
      return false;
    }
    return true;
  };

  // Save configuration without activating (just store the settings)
  const handleSaveOnly = async (e) => {
    e.preventDefault(); // Prevent form submission
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const payload = { ...formData, activate: false };
      // If no new API key provided and not local_llm, use a placeholder (backend will keep existing)
      if (formData.provider !== 'local_llm' && !payload.api_key && currentConfig) {
        payload.api_key = '***KEEP_EXISTING***';
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/llm-config`,
        payload
      );
      setCurrentConfig(response.data);
      
      const providerName = formData.provider === 'local_llm' ? 'Local LLM' : LLM_PROVIDERS[formData.provider]?.name;
      showToast(`${providerName} configuration saved!`, 'success');
    } catch (error) {
      console.error('Error saving LLM config:', error);
      showToast(error.response?.data?.detail || 'Failed to save configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Activate the provider for chat (uses STATIC config from code)
  const handleActivate = async (e) => {
    e.preventDefault(); // Prevent form submission
    setSaving(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/llm-config/activate`,
        { provider: formData.provider }
      );
      
      if (response.data.success) {
        showToast(response.data.message, 'success');
        // Reload config to reflect active state
        loadConfiguration();
      } else {
        showToast('Failed to activate provider', 'error');
      }
    } catch (error) {
      console.error('Error activating LLM config:', error);
      showToast(error.response?.data?.detail || 'Failed to activate provider', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (e) => {
    e.preventDefault(); // Prevent form submission
    if (!validateForm()) return;
    setTesting(true);
    setTestResponse('');
    try {
      const payload = {
        prompt: 'Say hello and introduce yourself in one sentence.',
        provider: formData.provider,
        model: formData.model,
        local_llm_stream: formData.local_llm_stream
      };
      if (formData.provider === 'local_llm') {
        payload.local_llm_url = formData.local_llm_url;
      } else {
        payload.api_key = formData.api_key || (currentConfig ? '***KEEP_EXISTING***' : '');
      }
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/llm-config/test`,
        payload
      );

      if (response.data.success) {
        setTestResponse(response.data.response || 'Connection OK');
        showToast('Connection test successful!', 'success');
        // Don't reload configuration here - keep the current form state
      } else {
        showToast(response.data.message || 'Test failed', 'error');
      }
    } catch (error) {
      console.error('Error testing LLM config:', error);
      showToast(error.response?.data?.detail || 'Failed to test connection', 'error');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="flex space-x-1">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
        </div>
      </div>
    );
  }

  const selectedProvider = LLM_PROVIDERS[formData.provider];
  const availableModels = selectedProvider?.models || [];

  return (
    <div className="p-4">
      {/* Toast */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in">
          <div className={`${toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'} text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 text-sm`}>
            {toast.type === 'success' ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-4">
        <h2 className="text-lg font-bold text-slate-900">LLM Model Configuration</h2>
        <p className="text-slate-500 text-xs mt-0.5">Configure AI language model for chat and analytics.</p>
      </div>

    

      {/* Provider Selection */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">LLM Provider</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(LLM_PROVIDERS).map(([key, provider]) => (
            <button key={key} type="button" onClick={() => handleProviderChange(key)}
              className={`p-3 rounded-lg border-2 transition-all text-left ${formData.provider === key ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
              <div className="flex items-center space-x-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-base ${formData.provider === key ? 'bg-emerald-100' : 'bg-slate-100'}`}>{provider.icon}</div>
                <div><p className={`font-medium text-xs ${formData.provider === key ? 'text-emerald-900' : 'text-slate-900'}`}>{provider.name}</p><p className="text-[10px] text-slate-500">{provider.description}</p></div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* API Configuration */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">API Configuration</h3>
        <div className="space-y-4">
          {/* Local LLM specific fields */}
          {formData.provider === 'local_llm' ? (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">LLM Server URL <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  value={formData.local_llm_url} 
                  onChange={(e) => handleInputChange('local_llm_url', e.target.value)}
                  placeholder={allProviderConfigs['local_llm']?.local_llm_url || "Enter your local LLM URL"}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 font-mono" 
                />
                <p className="text-[10px] text-slate-500 mt-1">The URL endpoint for your local LLM server</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Model <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  value={formData.model} 
                  onChange={(e) => handleInputChange('model', e.target.value)}
                  placeholder={allProviderConfigs['local_llm']?.model || "Enter model identifier"}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 font-mono" 
                />
                <p className="text-[10px] text-slate-500 mt-1">Model identifier (e.g., qwen2.5:7b, llama3.2:latest)</p>
              </div>
              <div className="flex items-center space-x-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={formData.local_llm_stream} 
                    onChange={(e) => handleInputChange('local_llm_stream', e.target.checked)}
                    className="sr-only peer" 
                  />
                  <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                </label>
                <span className="text-xs font-medium text-slate-700">Stream Responses</span>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">API Key <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type={showApiKey ? 'text' : 'password'} value={formData.api_key} onChange={(e) => handleInputChange('api_key', e.target.value)}
                    placeholder={currentConfig ? '••••••••••••••••••••••••' : selectedProvider.keyPlaceholder}
                    className="w-full px-3 py-2 pr-10 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 font-mono" />
                  <button type="button" onClick={() => setShowApiKey(!showApiKey)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">{showApiKey ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /> : <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></>}</svg>
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">{currentConfig ? 'Leave empty to keep existing. ' : ''}<a href={selectedProvider.helpUrl} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700">Get API key →</a></p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Model <span className="text-red-500">*</span></label>
                <ModelDropdown models={availableModels} selectedModel={formData.model} onSelect={(modelId) => handleInputChange('model', modelId)} isLoading={false} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Model Parameters - Hidden for Local LLM */}
      {formData.provider !== 'local_llm' && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Model Parameters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1"><label className="text-xs font-medium text-slate-700">Temperature</label><span className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded">{formData.temperature.toFixed(1)}</span></div>
              <input type="range" min="0" max="2" step="0.1" value={formData.temperature} onChange={(e) => handleInputChange('temperature', parseFloat(e.target.value))} className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600" />
              <div className="flex justify-between text-[10px] text-slate-500 mt-1"><span>Precise</span><span>Balanced</span><span>Creative</span></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><label className="text-xs font-medium text-slate-700">Max Tokens</label><span className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded">{formData.max_tokens.toLocaleString()}</span></div>
              <input type="range" min="256" max="8192" step="256" value={formData.max_tokens} onChange={(e) => handleInputChange('max_tokens', parseInt(e.target.value))} className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600" />
              <div className="flex justify-between text-[10px] text-slate-500 mt-1"><span>256</span><span>4,096</span><span>8,192</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <button type="button" onClick={handleTest} disabled={testing}
          className="px-4 py-2 text-xs border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 disabled:opacity-50 flex items-center space-x-1.5">
          {testing ? (<><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-emerald-600"></div><span>Testing...</span></>) : (<><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg><span>Test Connection</span></>)}
        </button>
        <div className="flex items-center space-x-2">
          {/* Save Button - Only saves without activating */}
          <button type="button" onClick={handleSaveOnly} disabled={saving}
            className="px-4 py-2 text-xs border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 disabled:opacity-50 flex items-center space-x-1.5">
            {saving ? (<><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-slate-600"></div><span>Saving...</span></>) : (<><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg><span>Save</span></>)}
          </button>
          {/* Activate Provider Button - Only activates, does not save */}
          <button type="button" onClick={handleActivate} disabled={saving}
            className="px-5 py-2 text-xs bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-lg font-medium hover:from-emerald-600 hover:to-cyan-700 disabled:opacity-50 flex items-center space-x-1.5">
            {saving ? (<><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Activating...</span></>) : (<><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg><span>Use {formData.provider === 'local_llm' ? 'Local LLM' : selectedProvider?.name || 'Provider'}</span></>)}
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LlmSettings;
